// src/lib/utils/hint.js
import { browser } from '$app/environment';
import { isOnboardingDone, userSession } from '$lib/stores.svelte.js';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { get } from 'svelte/store';

/** @typedef {{ elementSelector: string, title: string, description: string }} HintItem */

let pageLoad = 0;
if (browser) pageLoad = Date.now();

const HINT_STORAGE_KEY = 'closedHints';
/** @type {HintItem[]} */
let hintQueue = [];
let isShowing = false;
const HINT_DELAY_MS = 650;

/**
 * Shows the next hint in the queue.
 * @param {boolean} [withDelay=false]
 */
function showNextHint(withDelay = false) {
  if (browser && window.innerWidth < 768) {
    return;
  }
  if (!hintQueue.length) {
    isShowing = false;
    return;
  }
  isShowing = true;
  const wait = Math.max(0, 2400 - (Date.now() - pageLoad)) + (withDelay ? HINT_DELAY_MS : 0);
  setTimeout(() => {
    const currentHint = hintQueue[0];
    if (!currentHint) return;
    const { elementSelector, title, description } = currentHint;
    const driverInstance = driver({
      animate: true,
      allowClose: true,
      overlayOpacity: 0.2,
      stageRadius: 10,
      smoothScroll: true,
      popoverClass: 'ordalie-driver-theme'
    });
    driverInstance.highlight({
      element: elementSelector,
      popover: { title, description },
      onDeselected: () => {
        const stored = localStorage.getItem(HINT_STORAGE_KEY);
        /** @type {string[]} */
        const closedHints = stored ? JSON.parse(stored) : [];
        const hintId = getHintId(elementSelector, title);
        if (!closedHints.includes(hintId)) {
          closedHints.push(hintId);
          localStorage.setItem(HINT_STORAGE_KEY, JSON.stringify(closedHints));
        }
        hintQueue.shift();
        driverInstance.destroy();
        showNextHint(true);
      }
    });
  }, wait);
}

/**
 * Shows a one-time feature hint.
 * @param {string} elementSelector CSS selector of the element.
 * @param {string} title Hint title.
 * @param {string} description Hint description.
 * @param {boolean} [lazy=false] If true, only shows if no other hint is queued.
 * @param {string} [signature="L'équipe Ordalie"] Signature appended to description.
 */
export function showFeatureHint(elementSelector, title, description, lazy = false, signature = "L'équipe Ordalie") {
  if (!browser) return;
  if (!get(userSession)) return;
  if (!get(isOnboardingDone)) return;
  const hintId = getHintId(elementSelector, title);
  const stored = localStorage.getItem(HINT_STORAGE_KEY);
  /** @type {string[]} */
  const closedHints = stored ? JSON.parse(stored) : [];
  if (closedHints.includes(hintId)) return;
  if (lazy && hintQueue.length) return;
  hintQueue.push({
    elementSelector,
    title,
    description: `${description}<div style="font-family: 'Virgil', cursive!important; margin-top: 12px!important; opacity: 0.9;">- ${signature}</div>`
  });
  if (!isShowing) showNextHint(true);
}

/**
 * Generates a unique ID for a hint.
 * @param {string} elementSelector
 * @param {string} title
 * @returns {string}
 */
function getHintId(elementSelector, title) {
  const s = encodeURIComponent(`${elementSelector}:${title}`);
  return s.replace(/%/g, '').substring(0, 8);
}

/**
 * Resets hint storage.
 */
export function resetHintStorage() {
  localStorage.removeItem(HINT_STORAGE_KEY);
}